/**
 * GovEarner entry point (SPARC RFP platform)
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { App, initializeAPIClient, getApiBaseUrl, useAuthStore, useThemeStore, applyThemeVariables, getDefaultTheme, registerChunkLoadRecovery } from '@tracekit/core';
import { sparcConfig } from '../../../../config/sparc.config';
import { rfpAgentPlugin } from '@tracekit/plugin-rfp-agent';
import { CostAnalysisRoute } from './pages/CostAnalysisRoute';
import { OrganicContentRoute } from './pages/OrganicContentRoute';
import { OrganicDeskSettingsRoute } from './pages/OrganicDeskSettingsPage';
import { costingPlatformNav } from './pages/organicContentModel';
import { ORGANIC_DESK_SETTINGS_PATH } from './pages/organicDeskSettings';
import './index.css';

registerChunkLoadRecovery();

// SPARC RFP: force light mode before React renders.
// Writes light CSS vars and removes .dark class immediately so the very first paint
// is white, even before Zustand persist rehydrates from localStorage.
if (sparcConfig.theme?.defaultMode === 'light') {
  useThemeStore.getState().setTheme('light');
  // Persist 'light' to localStorage so rehydration never reinstates dark/system
  try {
    const raw = localStorage.getItem('tracekit-theme');
    const parsed = raw ? JSON.parse(raw) : {};
    localStorage.setItem('tracekit-theme', JSON.stringify({
      ...parsed,
      state: { ...(parsed?.state ?? {}), theme: 'light' },
      version: parsed?.version ?? 1,
    }));
  } catch { /* ignore */ }
}
// Remove .dark class before React renders (no flash of dark)
document.documentElement.classList.remove('dark');
// Apply SPARC light CSS vars right now so first-paint backgrounds are white
try {
  const lightTheme = getDefaultTheme('light', sparcConfig.theme!, sparcConfig);
  applyThemeVariables(lightTheme);
  useThemeStore.getState().setThemeConfig(lightTheme);
} catch { /* fallback: index.css :root vars cover this */ }

// Initialize API client (use VITE_API_URL from env / launch.json)
const apiUrl = getApiBaseUrl();
const client = initializeAPIClient(apiUrl);

// Clear workspace localStorage if tenant/project ID doesn't match config
// This ensures environment variables (VITE_TENANT_ID, VITE_PROJECT_ID) take precedence
if (import.meta.env.DEV) {
  try {
    const workspaceData = localStorage.getItem('tracekit-workspace');
    if (workspaceData) {
      const parsed = JSON.parse(workspaceData);
      const persistedTenantId = parsed?.state?.tenantId;
      const persistedProjectId = parsed?.state?.projectId;
      const expectedTenantId = sparcConfig.customization?.defaultTenantId;
      const expectedProjectId = sparcConfig.customization?.defaultProjectId;
      
      // Only enforce defaults if environment variables are explicitly set
      const envTenantId = import.meta.env.VITE_TENANT_ID;
      const envProjectId = import.meta.env.VITE_PROJECT_ID;
      
      let shouldClear = false;
      const reasons: string[] = [];
      
      // If env variables are set, they take precedence - clear if mismatch
      if (envTenantId && persistedTenantId && persistedTenantId !== expectedTenantId) {
        shouldClear = true;
        reasons.push(`Tenant ID mismatch (persisted: ${persistedTenantId}, expected: ${expectedTenantId})`);
      }
      
      if (envProjectId && persistedProjectId && persistedProjectId !== expectedProjectId) {
        shouldClear = true;
        reasons.push(`Project ID mismatch (persisted: ${persistedProjectId}, expected: ${expectedProjectId})`);
      }
      
      if (shouldClear) {
        console.log('[Main] Clearing workspace localStorage:', reasons.join(', '));
        localStorage.removeItem('tracekit-workspace');
      } else {
        console.log('[Main] Workspace localStorage valid:', {
          persistedTenantId,
          persistedProjectId,
          expectedTenantId,
          expectedProjectId
        });
      }
    }
  } catch (e) {
    console.warn('[Main] Error checking workspace localStorage:', e);
  }
}

// For development: Auto-set master key if available from env or localStorage
if (import.meta.env.DEV) {
  // Check for master key in environment variable (VITE_MASTER_API_KEY)
  const envMasterKey = import.meta.env.VITE_MASTER_API_KEY;
  if (envMasterKey) {
    console.log('[Main] Setting master API key from environment variable:', envMasterKey.substring(0, 20) + '...');
    client.setApiKey(envMasterKey);
    // Also set in auth store for persistence
    try {
      useAuthStore.getState().setApiKey(envMasterKey);
      console.log('[Main] API key also set in auth store');
    } catch (err) {
      console.warn('[Main] Failed to set API key in auth store:', err);
    }
  } else {
    console.warn('[Main] VITE_MASTER_API_KEY not found in environment variables');
    // Check localStorage for persisted API key
    try {
      const authData = localStorage.getItem('tracekit-auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        if (parsed.state?.apiKey) {
          console.log('[Main] Restoring API key from localStorage');
          client.setApiKey(parsed.state.apiKey);
        } else if (parsed.state?.accessToken) {
          console.log('[Main] Restoring access token from localStorage');
          client.setAccessToken(parsed.state.accessToken);
        } else {
          console.warn('[Main] No API key or access token found in localStorage');
        }
      } else {
        console.warn('[Main] No auth data in localStorage');
      }
    } catch (e) {
      console.error('[Main] Error reading localStorage:', e);
    }
  }
  
  // Log final auth state
  console.log('[Main] API client auth state:', {
    hasAuth: (client as any).hasAuth ? (client as any).hasAuth() : 'unknown',
    apiKeyLength: (client as any).apiKey ? (client as any).apiKey.length : 0,
  });
}

// Override tenant and project IDs from environment variables if provided
// This allows launch.json configurations to take precedence
if (import.meta.env.VITE_TENANT_ID) {
  console.log('[Main] Overriding tenant ID from environment:', import.meta.env.VITE_TENANT_ID);
  if (sparcConfig.customization) {
    sparcConfig.customization.defaultTenantId = import.meta.env.VITE_TENANT_ID;
  }
}

if (import.meta.env.VITE_PROJECT_ID) {
  console.log('[Main] Overriding project ID from environment:', import.meta.env.VITE_PROJECT_ID);
  if (sparcConfig.customization) {
    sparcConfig.customization.defaultProjectId = import.meta.env.VITE_PROJECT_ID;
  }
}

console.log('╔══════════════════════════════════════════════════════════════╗');
console.log('║  GovEarner - TraceKit Initialized                           ║');
console.log('╠══════════════════════════════════════════════════════════════╣');
console.log('║  API URL:', apiUrl.padEnd(49), '║');
console.log('║  Frontend:', (typeof window !== 'undefined' ? window.location.origin : 'N/A').padEnd(49), '║');
console.log('║  Tenant ID:', (sparcConfig.customization?.defaultTenantId || 'N/A').padEnd(47), '║');
console.log('║  Project ID:', (sparcConfig.customization?.defaultProjectId || 'N/A').padEnd(46), '║');
console.log('╚══════════════════════════════════════════════════════════════╝');

// Add RFP Agent plugin with SPARC configuration
// Filter sidebar items based on feature flags
const flags = sparcConfig.customization?.featureFlags;

const sparcRfpPlugin = {
  ...rfpAgentPlugin,
  onInit: async (context: Parameters<NonNullable<typeof rfpAgentPlugin.onInit>>[0]) => {
    await rfpAgentPlugin.onInit?.(context);
    try {
      const { sparcHelpContent } = await import('./help/sparcHelpContent');
      const { helpContentRegistry } = await import('@tracekit/core');
      if (sparcHelpContent?.length) {
        helpContentRegistry.register('sparc-rfp', sparcHelpContent);
        console.log(`[SPARC RFP] Registered ${sparcHelpContent.length} help articles in Help Center`);
      }
    } catch (error) {
      console.warn('[SPARC RFP] Failed to register help content:', error);
    }
  },
  routes: [
    ...(rfpAgentPlugin.routes || []),
    {
      path: '/rfp/cost-analysis',
      component: CostAnalysisRoute,
    },
    {
      path: '/rfp/organic-content',
      component: OrganicContentRoute,
    },
    {
      path: ORGANIC_DESK_SETTINGS_PATH,
      component: OrganicDeskSettingsRoute,
    },
  ],
  sidebarItems: [
    ...(rfpAgentPlugin.sidebarItems || []).filter((item) => {
    if (item.path === '/rfp/workplan-execution' && !flags?.enableWorkPlanExecution) {
      console.log('[Main] Filtering out WorkPlan sidebar item (disabled)');
      return false;
    }
    if (item.path === '/rfp/dashboard' && !flags?.enableExecutiveDashboard) {
      console.log('[Main] Filtering out Executive Dashboard sidebar item (disabled)');
      return false;
    }
    if ((item.path === '/rfp/staff-data' || item.label === 'Staff Management') && !flags?.showStaffManagementStandalone) {
      console.log('[Main] Filtering out Staff Management sidebar item (disabled)');
      return false;
    }
    return true;
  }),
    // SPARC tenant only (enableCostingPlatform). Order: Cost Analysis 3.5, Organic 4.5, Data Pipeline 5.
    ...costingPlatformNav(Boolean(flags?.enableCostingPlatform)),
  ],
};

sparcConfig.plugins = [sparcRfpPlugin];

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App config={sparcConfig} />
  </React.StrictMode>
);
